/* eslint-disable no-shadow */
/* eslint-disable consistent-return */
import { useState, useCallback, useEffect } from "react";
import { useSelector } from "react-redux";
import { selectDateFilter } from "../redux/selectors/dateFilterSelector";
import { API } from "../apis/api";
import {
  getTicketURL,
  transformApiDataToRows,
  VIEW_TYPE,
} from "../utils/ticketUtils";

const useTicketData = (
  ticketStatus,
  enableSearch = false,
  viewType = VIEW_TYPE.ASSIGNED,
  initialPage = 0,
  initialRowsPerPage = 10
) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [allRows, setAllRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(initialPage);
  const [rowsPerPage, setRowsPerPage] = useState(initialRowsPerPage);

  const { startDate, endDate } = useSelector(selectDateFilter);
  const userId = useSelector((state)=>state.auth.user.userId)

  // Debounce search query if search is enabled
  useEffect(() => {
    if (!enableSearch) return;

    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery, enableSearch]);

  // Reset page when filters change
  useEffect(() => {
    setPage(0);
  }, [debouncedSearchQuery, startDate, endDate]);

  const formatDateForAPI = (date) => {
    if (!date) return null;
    return new Date(date).toISOString();
  };

  const fetchTickets = async (startDate, endDate, searchText = "") => {
    try {
      const fromDate = startDate || "2024-06-05T05:30:00Z";
      const toDate = endDate || "2025-07-29T23:59:59Z";

      const apiUrl = getTicketURL(viewType);

      const requestBody = {
        ticket_status: ticketStatus,
        from_datetime: fromDate,
        to_datetime: toDate,
        limit: rowsPerPage,
        offset: page * rowsPerPage,
        sub:userId,
      };

      if (enableSearch && searchText && searchText.trim()) {
        requestBody.search_text = searchText.trim();
      }

      const response = await API.post(apiUrl, requestBody);
      return response.data;
    } catch (error) {
      console.error("Error fetching tickets:", error);
      throw error;
    }
  };

  const loadTickets = useCallback(
    async (isRefresh = false, searchText = "") => {
      try {
        if (isRefresh) {
          setIsRefreshing(true);
        } else {
          setLoading(true);
        }
        setError(null);

        const formattedStartDate = formatDateForAPI(startDate);
        const formattedEndDate = formatDateForAPI(endDate);

        const apiData = await fetchTickets(
          formattedStartDate,
          formattedEndDate,
          searchText
        );
        const transformedRows = transformApiDataToRows(apiData);
        setAllRows(transformedRows);
        setTotal(apiData.total || 0);
      } catch (err) {
        console.error("Failed to load tickets:", err);
        setError(err.message);
        setAllRows([]);
        setTotal(0);
      } finally {
        if (isRefresh) {
          setIsRefreshing(false);
        } else {
          setLoading(false);
        }
      }
    },
    [
      startDate,
      endDate,
      ticketStatus,
      enableSearch,
      viewType,
      page,
      rowsPerPage,
    ]
  );

  const handleRefresh = useCallback(() => {
    const searchText = enableSearch ? debouncedSearchQuery : "";
    loadTickets(true, searchText);
  }, [loadTickets, debouncedSearchQuery, enableSearch]);

  const handleSearchQuery = useCallback((query) => {
    setSearchQuery(query.trim());
  }, []);

  const handleChangePage = useCallback((newPage) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  }, []);

  useEffect(() => {
    const searchText = enableSearch ? debouncedSearchQuery : "";
    loadTickets(false, searchText);
  }, [loadTickets, debouncedSearchQuery, viewType]);

  return {
    searchQuery,
    allRows,
    setAllRows,
    loading,
    isRefreshing,
    error,
    handleRefresh,
    handleSearchQuery,
    startDate,
    endDate,
    total,
    page,
    rowsPerPage,
    handleChangePage,
    handleChangeRowsPerPage,
    setPage,
  };
};

export default useTicketData;
